import React, { useState, useRef, useEffect } from 'react';
import { FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import "./TimePicker.css";

const TimePicker = ({ 
  value, 
  onChange, 
  timeFormat = '12-hour',
  placeholder = "Select time",
  className = "",
  disabled = false,
  timeIntervals = 15
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('AM');
  const dropdownRef = useRef(null);

  // Generate hour options based on time format
  const getHourOptions = () => {
    if (timeFormat === '12-hour') {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    } else {
      return Array.from({ length: 24 }, (_, i) => i);
    }
  };

  // Generate minute options based on timeIntervals
  const getMinuteOptions = () => {
    const minutes = [];
    for (let i = 0; i < 60; i += timeIntervals) {
      minutes.push(i);
    }
    return minutes;
  };

  // Parse current value to set initial state
  useEffect(() => {
    if (value) {
      try {
        const [hours, minutes] = value.split(':').map(Number);
        if (timeFormat === '12-hour') {
          const period = hours >= 12 ? 'PM' : 'AM';
          const displayHour = hours === 0 ? 12 : (hours > 12 ? hours - 12 : hours);
          setSelectedHour(displayHour.toString());
          setSelectedMinute(minutes.toString().padStart(2, '0'));
          setSelectedPeriod(period);
        } else {
          setSelectedHour(hours.toString().padStart(2, '0'));
          setSelectedMinute(minutes.toString().padStart(2, '0'));
        }
      } catch (error) {
        console.error('Error parsing time value:', error);
      }
    } else {
      setSelectedHour('');
      setSelectedMinute('');
      setSelectedPeriod('AM');
    }
  }, [value, timeFormat]);

  // Update parent component when selections change
  useEffect(() => {
    if (selectedHour && selectedMinute) {
      let hours = parseInt(selectedHour);
      const minutes = parseInt(selectedMinute);

      if (timeFormat === '12-hour') {
        if (selectedPeriod === 'PM' && hours !== 12) {
          hours += 12;
        } else if (selectedPeriod === 'AM' && hours === 12) {
          hours = 0;
        }
      }

      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      
      // Only call onChange if the value actually changed
      if (value !== timeString) {
        onChange(timeString);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHour, selectedMinute, selectedPeriod, timeFormat]); // Removed onChange from deps to prevent infinite loop

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getDisplayValue = () => {
    if (!selectedHour || !selectedMinute) return '';
    
    if (timeFormat === '12-hour') {
      return `${selectedHour}:${selectedMinute} ${selectedPeriod}`;
    } else {
      return `${selectedHour.padStart(2, '0')}:${selectedMinute}`;
    }
  };

  const handleHourChange = (hour) => {
    setSelectedHour(hour.toString());
  };

  const handleMinuteChange = (minute) => {
    setSelectedMinute(minute.toString().padStart(2, '0'));
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const DropdownOption = ({ onClick, isSelected, children }) => (
    <div
      className={`px-3 py-2 cursor-pointer hover:bg-teal-50 transition-colors ${
        isSelected ? 'bg-teal-100 text-teal-700 font-medium' : 'text-gray-700'
      }`}
      onClick={onClick}
    >
      {children}
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus-within:outline-none focus-within:ring-2 focus-within:ring-teal-500 cursor-pointer ${
          className
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <span className={value ? 'text-gray-900' : 'text-gray-500'}>
            {getDisplayValue() || placeholder}
          </span>
          <div className="flex items-center space-x-1">
            <FiClock className="text-gray-400" size={16} />
            {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </div>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
          <div className="grid grid-cols-3 gap-1 p-2">
            {/* Hours */}
            <div className="space-y-1">
              <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Hour
              </div>
              {getHourOptions().map((hour) => (
                <DropdownOption
                  key={hour}
                  value={hour}
                  isSelected={selectedHour === hour.toString()}
                  onClick={() => handleHourChange(hour)}
                >
                  {hour.toString().padStart(2, '0')}
                </DropdownOption>
              ))}
            </div>

            {/* Minutes */}
            <div className="space-y-1">
              <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Minute
              </div>
              {getMinuteOptions().map((minute) => (
                <DropdownOption
                  key={minute}
                  value={minute}
                  isSelected={selectedMinute === minute.toString().padStart(2, '0')}
                  onClick={() => handleMinuteChange(minute)}
                >
                  {minute.toString().padStart(2, '0')}
                </DropdownOption>
              ))}
            </div>

            {/* AM/PM (only for 12-hour format) */}
            {timeFormat === '12-hour' && (
              <div className="space-y-1">
                <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Period
                </div>
                <DropdownOption
                  value="AM"
                  isSelected={selectedPeriod === 'AM'}
                  onClick={() => handlePeriodChange('AM')}
                >
                  AM
                </DropdownOption>
                <DropdownOption
                  value="PM"
                  isSelected={selectedPeriod === 'PM'}
                  onClick={() => handlePeriodChange('PM')}
                >
                  PM
                </DropdownOption>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
